<script setup lang="ts">
/**
 * ResidentDashboardView — ports resident-dashboard.html.
 *
 * Active path: calls useResidentIssues() + useResidentAnnouncements() in parallel.
 * Pending path: short-circuits — shows PendingApprovalBanner only.
 *
 * Visual rules:
 * - All IDs, counts, times: font-mono tabular-nums
 * - No teal except AI content markers (none here — no AI-generated content on dashboard)
 * - Severity color map fixed per CLAUDE.md
 * - Mobile-first: single-column at ≤900px
 */
import { computed } from 'vue';
import { useAuth } from '@/stores/auth';
import { useResidentIssues, useResidentAnnouncements } from '@/composables/useResident';
import PendingApprovalBanner from '@/components/resident/PendingApprovalBanner.vue';
import SubmissionCard from '@/components/resident/SubmissionCard.vue';
import AnnouncementCard from '@/components/resident/AnnouncementCard.vue';

const auth = useAuth();

// If pending, don't fetch data — board hasn't approved yet.
const isPending = computed(() => auth.isPending);

// Fetch data (the composables guard against calling the API when store says pending).
const { items: issues, isLoading: issuesLoading, error: issuesError } = useResidentIssues({ limit: 10 });
const { items: announcements, isLoading: annLoading, error: annError } = useResidentAnnouncements({ limit: 5 });

const userName = computed(() => {
  const n = auth.user?.displayName ?? '';
  return n.split(/\s+/)[0] ?? 'there';
});

const greeting = computed(() => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
});
</script>

<template>
  <div class="rb-wrap" data-testid="resident-dashboard-view">
    <!-- Pending approval: short-circuit, show banner only -->
    <div v-if="isPending">
      <PendingApprovalBanner :user-name="userName" />
      <div style="text-align: center; padding: 40px 24px 0; font-size: 12.5px; color: var(--muted);">
        You'll have full access once the board approves your account.
        <a href="/portal" style="color: var(--navy-700); font-weight: 600;">Report an issue anonymously</a>
        in the meantime.
      </div>
    </div>

    <!-- Active resident — full dashboard -->
    <template v-else>
      <!-- Welcome strip -->
      <div class="welcome">
        <div>
          <h1>{{ greeting }}, {{ userName }}</h1>
          <p class="lead">
            <span class="verified-pill">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
              Verified resident
            </span>
          </p>
        </div>
      </div>

      <!-- Quick CTA: report an issue -->
      <a href="/portal" class="quick-cta" style="text-decoration: none;" data-testid="quick-report-cta">
        <div class="quick-cta-text">
          <h2 class="quick-cta-title">See something that needs the board's attention?</h2>
          <p class="quick-cta-sub">Pool, gate, parking, safety, anything else — submit a quick report. Urgent issues text the right board member immediately.</p>
        </div>
        <span class="btn-light">
          Report an issue
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" style="width:14px;height:14px;">
            <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
          </svg>
        </span>
      </a>

      <!-- Two-column grid: submissions left, right rail -->
      <div class="grid-resident">

        <!-- LEFT: My submissions -->
        <div>
          <div class="res-section-head">
            <h2>My submissions</h2>
            <span class="count font-mono tabular-nums">
              {{ issues.length }} total
            </span>
          </div>

          <!-- Loading state -->
          <div v-if="issuesLoading" class="empty-mini" data-testid="issues-loading">
            Loading submissions…
          </div>

          <!-- Error state -->
          <div v-else-if="issuesError" class="empty-mini text-red-600" data-testid="issues-error" role="alert">
            {{ issuesError }}
          </div>

          <!-- Empty state -->
          <div v-else-if="issues.length === 0" class="empty-mini" data-testid="issues-empty">
            No submissions yet.
            <a href="/portal" style="color: var(--navy-700); font-weight: 500;">Report an issue</a>
            to get started.
          </div>

          <!-- Submission cards -->
          <template v-else>
            <SubmissionCard
              v-for="issue in issues"
              :key="issue.id"
              :issue="issue"
            />
          </template>
        </div>

        <!-- RIGHT: Announcements rail -->
        <aside>
          <div class="rail-card">
            <h3>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--navy-700);">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="M22 4L12 14.01l-3-3" />
              </svg>
              Community updates
            </h3>

            <!-- Loading -->
            <div v-if="annLoading" class="empty-mini" data-testid="announcements-loading">Loading…</div>

            <!-- Error (degrade gracefully per spec §5.6) -->
            <div v-else-if="annError" class="empty-mini" data-testid="announcements-error">
              Announcements unavailable
            </div>

            <!-- Announcements -->
            <template v-else-if="announcements.length > 0">
              <AnnouncementCard
                v-for="(a, i) in announcements"
                :key="a.id"
                :announcement="a"
                :eyebrow="i === 0 ? 'Pinned' : 'Notice'"
              />
              <a href="#" style="display: block; text-align: center; margin-top: 8px; font-size: 12.5px; font-weight: 500; color: var(--navy-700);">
                All community updates &rarr;
              </a>
            </template>

            <!-- Empty -->
            <div v-else class="empty-mini" data-testid="announcements-empty">
              No announcements yet.
            </div>
          </div>

          <!-- After-hours emergency rail card -->
          <div class="rail-card" style="background: var(--bg-soft);" data-testid="emergency-card">
            <h3>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--muted);">
                <circle cx="12" cy="12" r="9" /><path d="M12 6v6l4 2" />
              </svg>
              After-hours emergency
            </h3>
            <div style="font-size: 12.5px; color: var(--fg-soft); line-height: 1.55;">
              For life-threatening emergencies, call <strong>911</strong> first.
              For after-hours HOA urgencies (gate, leak, safety), text:
              <div class="font-mono tabular-nums" style="font-size: 15px; font-weight: 700; color: var(--fg); margin-top: 8px;">
                (512) 555-0199
              </div>
            </div>
          </div>
        </aside>
      </div>

      <!-- Footer -->
      <div style="text-align: center; padding: 40px 24px 0; font-size: 12px; color: var(--muted-2); border-top: 1px solid var(--border); margin-top: 40px;">
        HOA Watch ·
        <a href="#" style="color: var(--muted);">Bylaws</a> ·
        <a href="#" style="color: var(--muted);">Privacy</a> ·
        Powered by HOA Watch
      </div>
    </template>
  </div>
</template>

<style scoped>
.rb-wrap {
  max-width: 980px;
  margin: 0 auto;
  padding: 32px 24px 80px;
}

/* Welcome strip */
.welcome {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 28px;
}
.welcome h1 {
  font-family: var(--font-display);
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0 0 4px;
  line-height: 1.1;
}
.welcome .lead { color: var(--muted); font-size: 14.5px; }
@media (max-width: 600px) {
  .welcome h1 { font-size: 22px; }
}

/* Verified pill — green = verified/resolved (per color map) */
.verified-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px 4px 8px;
  background: var(--green-100);
  color: var(--green-700);
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 600;
  margin-left: 8px;
}
.verified-pill svg { width: 12px; height: 12px; }

/* Quick CTA navy banner */
.quick-cta {
  background: linear-gradient(180deg, var(--navy-900), var(--navy-800));
  color: white;
  border-radius: var(--r-xl, 16px);
  padding: 28px 32px;
  display: flex;
  gap: 24px;
  align-items: center;
  position: relative;
  overflow: hidden;
  margin-bottom: 28px;
}
.quick-cta::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: radial-gradient(circle at 85% 30%, color-mix(in oklch, var(--teal-500), transparent 60%), transparent 50%);
  pointer-events: none;
}
.quick-cta > * { position: relative; }
.quick-cta-text { flex: 1; }
.quick-cta-title {
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin: 0 0 4px;
}
.quick-cta-sub { color: color-mix(in oklch, white, var(--navy-100) 40%); font-size: 13.5px; line-height: 1.5; max-width: 480px; }
.btn-light {
  background: white;
  color: var(--navy-800);
  border: none;
  height: 44px;
  padding: 0 18px;
  border-radius: var(--r-md, 8px);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  white-space: nowrap;
}
.btn-light:hover { background: var(--navy-50); }
@media (max-width: 600px) {
  .quick-cta { padding: 22px; flex-direction: column; align-items: flex-start; gap: 16px; }
}

/* Two-col grid */
.grid-resident {
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 20px;
}
@media (max-width: 900px) { .grid-resident { grid-template-columns: 1fr; } }

/* Section header */
.res-section-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 14px;
}
.res-section-head h2 {
  font-family: var(--font-display);
  font-size: 17px;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.01em;
}
.count { font-size: 11.5px; color: var(--muted-2); font-weight: 600; }

/* Right rail cards */
.rail-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--r-lg, 12px);
  padding: 18px 20px;
  margin-bottom: 14px;
}
.rail-card h3 {
  font-size: 13px;
  font-weight: 700;
  margin: 0 0 12px;
  letter-spacing: -0.005em;
  display: flex;
  align-items: center;
  gap: 8px;
}
.rail-card h3 svg { width: 14px; height: 14px; }

/* Empty state */
.empty-mini {
  text-align: center;
  padding: 24px 16px;
  color: var(--muted);
  font-size: 12.5px;
}
</style>
